import { getApiBaseUrl } from './api-base';

const API_URL = getApiBaseUrl();

const SESSION_EXPIRED_ERRORS = new Set(['Invalid token', 'No token provided', 'Not authenticated']);

let isRedirectingToLogin = false;

function getToken(): string | null {
  try {
    const saved = localStorage.getItem('admin_auth');
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.token;
    }
  } catch {}
  return null;
}

function clearAdminSession(): void {
  localStorage.removeItem('admin_auth');
}

/**
 * Clears stale JWT from the browser and sends the user to /login.
 */
function handleExpiredSession(): void {
  clearAdminSession();
  if (isRedirectingToLogin) {
    return;
  }
  const path = window.location.pathname;
  if (path === '/login' || path.startsWith('/auth/')) {
    return;
  }
  isRedirectingToLogin = true;
  window.location.assign('/login');
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {})
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as { error?: string }));
    const errorMessage = typeof body.error === 'string' ? body.error : '';
    if (res.status === 401 && token && SESSION_EXPIRED_ERRORS.has(errorMessage)) {
      handleExpiredSession();
    }
    throw new Error(errorMessage || `HTTP ${res.status}`);
  }
  return res.json();
}

export interface EquipmentColumn {
  id: string;
  label: string;
  sortOrder: number;
}

export interface ApiTour {
  id: number;
  slug: string;
  name: string;
  organizer: string;
  country: string;
  city: string;
  region: string;
  category: string;
  season: string;
  price: number;
  currency: string;
  is_free: boolean;
  price_note: string;
  org_fee: number;
  food_base_fee: number;
  gas_fee_per_person: number;
  duration: number;
  date_start: string;
  date_end: string;
  time_start: string;
  total_seats: number;
  booked_seats: number;
  rating: number;
  image: string;
  description: string;
  highlights: string | string[];
  route: string | { time: string; title: string; description: string }[];
  what_to_bring: string | string[];
  contact: string | { phone: string; telegram: string };
  maps: string | { label: string; url: string }[];
  latitude: number | null;
  longitude: number | null;
  is_completed?: boolean;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface TourInput {
  slug: string;
  name: string;
  organizer?: string;
  country?: string;
  city: string;
  region: string;
  category?: string;
  season?: string;
  price?: number;
  currency?: string;
  is_free?: boolean;
  price_note?: string;
  org_fee?: number;
  food_base_fee?: number;
  gas_fee_per_person?: number;
  duration?: number;
  date_start: string;
  date_end: string;
  time_start: string;
  total_seats?: number;
  booked_seats?: number;
  rating?: number;
  image?: string;
  description?: string;
  highlights?: string[];
  route?: { time: string; title: string; description: string }[];
  what_to_bring?: string[];
  contact?: { phone: string; telegram: string };
  maps?: { label: string; url: string }[];
  latitude?: number | null;
  longitude?: number | null;
  location_ids?: number[];
}

function parseJsonField<T>(val: unknown, fallback: T): T {
  if (Array.isArray(val) || (typeof val === 'object' && val !== null)) return val as T;
  if (typeof val === 'string') {
    try {
      return JSON.parse(val) as T;
    } catch {
      return fallback;
    }
  }
  return fallback;
}

export function normalizeTour(t: ApiTour) {
  return {
    ...t,
    highlights: parseJsonField<string[]>(t.highlights, []),
    route: parseJsonField<{ time: string; title: string; description: string; latitude?: number; longitude?: number }[]>(t.route, []),
    what_to_bring: parseJsonField<string[]>(t.what_to_bring, []),
    contact: parseJsonField<{ phone: string; telegram: string }>(t.contact, { phone: '', telegram: '' }),
    maps: parseJsonField<{ label: string; url: string }[]>(t.maps, []),
    latitude: t.latitude ?? null,
    longitude: t.longitude ?? null,
    location_ids: (t as any).location_ids ?? [],
    locations: (t as any).locations ?? []
  };
}

export interface PipelineData {
  stages: string[];
  labels: Record<string, string>;
  grouped: Record<string, (ApiTour & { user: any; pipelineLog: any[] })[]>;
  total: number;
}

export interface TourParticipantBooking {
  id: number;
  seats: number;
  status: string;
  note: string | null;
  equipment?: Record<string, string> | null;
  notifiedAt: string | null;
  calledAt: string | null;
  callResult: string | null;
  driverId: number | null;
  travelMode?: 'car' | 'bus' | string;
  createdAt: string;
  prepayPaid: boolean;
  prepayPaidAt: string | null;
  gasPaid: boolean;
  gasPaidAt: string | null;
  user: {
    id: number;
    name: string;
    email: string;
    phone: string | null;
    telegramUsername: string | null;
    vkId: string | null;
    readyForTrips: boolean;
    avatarUrl: string | null;
  };
  driver?: { id: number; user: { id: number; name: string; telegramUsername?: string | null; vkId?: string | null } } | null;
}

export interface Location {
  id: number;
  slug: string;
  name: string;
  description: string;
  category: string;
  region: string;
  latitude: number;
  longitude: number;
  image: string;
  difficulty: string;
  best_season: string | null;
  distance_from_ekb: number | null;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

export interface LocationInput {
  slug: string;
  name: string;
  description?: string;
  category?: string;
  region?: string;
  latitude: number;
  longitude: number;
  image?: string;
  difficulty?: string;
  best_season?: string | null;
  distance_from_ekb?: number | null;
  is_published?: boolean;
}

export interface AdminUser {
  id: number;
  name: string;
  email: string;
  role: string;
  vkId?: string | null;
  permissions?: string[];
  createdAt: string;
}

export interface Role {
  id: number;
  name: string;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
}

export interface PermissionGroup {
  group: string;
  permissions: { key: string; label: string }[];
}

export interface AdminLoginResponse {
  user: { id: number; name: string; email: string; role?: string; permissions?: string[] };
  token: string;
}

export interface AuthLoginHistoryItem {
  id: number;
  createdAt: string;
  appTarget: string;
  loginMethod: string;
  success: boolean;
  ipAddress: string | null;
  userAgent: string | null;
  failureReason: string | null;
  emailAttempt: string | null;
  userId: number | null;
}

export interface OperationLogItem {
  id: number;
  createdAt: string;
  actorUserId: number | null;
  actorRole: string | null;
  appTarget: string | null;
  actionType: string;
  entityType: string;
  entityId: string | null;
  summary: string;
  payloadPreview: string | null;
  ipAddress: string | null;
  userAgent: string | null;
}

export const api = {
  login: (email: string, password: string) =>
    request<AdminLoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    }),

  getMe: () => request<{ user: AdminLoginResponse['user'] }>('/api/auth/me'),

  oauthVkComplete: (key: string) =>
    request<AdminLoginResponse & { target: string }>('/api/auth/oauth/vk/complete', {
      method: 'POST',
      body: JSON.stringify({ key })
    }),

  getTours: (params?: { search?: string; season?: string }) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.season && params.season !== 'all') qs.set('season', params.season);
    return request<{ tours: ApiTour[]; total: number }>(`/api/tours?${qs}`);
  },

  getTour: (slug: string) => request<ApiTour>(`/api/tours/${slug}`),

  getMetrics: () =>
    request<{ total_tours: number; total_participants: number; upcoming_trips: number }>('/api/tours/metrics'),

  createTour: (tour: TourInput) =>
    request<ApiTour>('/api/tours', { method: 'POST', body: JSON.stringify(tour) }),

  duplicateTour: (id: number) =>
    request<ApiTour>(`/api/tours/${id}/duplicate`, { method: 'POST' }),

  completeTour: (id: number) =>
    request<ApiTour>(`/api/tours/${id}/complete`, { method: 'POST' }),

  reopenTour: (id: number) =>
    request<ApiTour>(`/api/tours/${id}/reopen`, { method: 'POST' }),

  createTourFromLocations: (data: { location_ids: number[]; date_start: string; date_end?: string }) =>
    request<ApiTour>('/api/tours/from-locations', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  updateTour: (id: number, tour: Partial<TourInput>) =>
    request<ApiTour>(`/api/tours/${id}`, { method: 'PUT', body: JSON.stringify(tour) }),

  deleteTour: (id: number) =>
    request<{ deleted: boolean }>(`/api/tours/${id}`, { method: 'DELETE' }),

  getTourParticipants: (id: number) =>
    request<{
      tour: {
        id: number;
        name: string;
        slug: string;
        dateStart: string;
        totalSeats: number;
        bookedSeats: number;
        image: string;
        equipmentColumns?: EquipmentColumn[];
        isCompleted?: boolean;
        completedAt?: string | null;
        orgFee?: number;
        foodBaseFee?: number;
        gasFeePerPerson?: number;
      };
      drivers: any[];
      confirmed: TourParticipantBooking[];
      waitlist: TourParticipantBooking[];
      pending: TourParticipantBooking[];
      candidates: any[];
      stats: {
        totalConfirmed: number;
        totalWaitlist: number;
        totalSeats: number;
        totalNotified: number;
        totalCalled: number;
        totalReached: number;
        totalNoAnswer: number;
        candidatesInvited: number;
        candidatesDeclined: number;
        candidatesPending: number;
      };
    }>(`/api/tours/${id}/participants`),

  updateEquipmentColumns: (tourId: number, columns: { id?: string; label: string; sortOrder?: number }[]) =>
    request<{ equipmentColumns: EquipmentColumn[] }>(`/api/tours/${tourId}/equipment-columns`, {
      method: 'PUT',
      body: JSON.stringify({ columns })
    }),

  updateBookingEquipment: (bookingId: number, equipment: Record<string, string>) =>
    request<{ id: number; equipment: Record<string, string> }>(`/api/bookings/${bookingId}/equipment`, {
      method: 'PUT',
      body: JSON.stringify({ equipment })
    }),

  updateBookingPayments: (bookingId: number, data: { prepayPaid?: boolean; gasPaid?: boolean }) =>
    request<TourParticipantBooking>(`/api/bookings/${bookingId}/payments`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  notifyBooking: (id: number) =>
    request<any>(`/api/bookings/${id}/notify`, { method: 'PUT' }),

  callBooking: (id: number, callResult: string) =>
    request<any>(`/api/bookings/${id}/call`, { method: 'PUT', body: JSON.stringify({ callResult }) }),

  getCandidates: (tourId: number) =>
    request<any[]>(`/api/tours/${tourId}/candidates`),

  createCandidate: (tourId: number, data: { name: string; phone?: string; telegramId?: string; telegramUsername?: string; status?: string }) =>
    request<any>(`/api/tours/${tourId}/candidates`, { method: 'POST', body: JSON.stringify(data) }),

  updateCandidate: (tourId: number, candidateId: number, data: any) =>
    request<any>(`/api/tours/${tourId}/candidates/${candidateId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteCandidate: (tourId: number, candidateId: number) =>
    request<any>(`/api/tours/${tourId}/candidates/${candidateId}`, { method: 'DELETE' }),

  sendTelegramCandidate: (tourId: number, candidateId: number, data: { templateId?: number; customText?: string; imageUrl?: string }) =>
    request<any>(`/api/tours/${tourId}/candidates/${candidateId}/send-telegram`, { method: 'POST', body: JSON.stringify(data) }),

  sendTelegramCandidateBulk: (tourId: number, data: { templateId?: number; customText?: string; imageUrl?: string }) =>
    request<{ sent: number; skipped: number; errors: any[] }>(`/api/tours/${tourId}/candidates/send-telegram-bulk`, { method: 'POST', body: JSON.stringify(data) }),

  autoAddCandidates: (tourId: number) =>
    request<{ added: number; skipped: number; total: number }>(`/api/tours/${tourId}/candidates/auto-add`, { method: 'POST' }),

  getImportGroups: (tourId: number) =>
    request<string[]>(`/api/tours/${tourId}/candidates/import-groups`),

  addImportedCandidates: (tourId: number, groupName?: string) =>
    request<{ added: number; skipped: number; total: number; groupName: string }>(`/api/tours/${tourId}/candidates/add-imported`, {
      method: 'POST', body: JSON.stringify({ groupName })
    }),

  addRegulars: (tourId: number) =>
    request<{ added: number; skipped: number; total: number }>(`/api/tours/${tourId}/candidates/add-regulars`, { method: 'POST' }),

  promoteCandidate: (tourId: number, candidateId: number) =>
    request<{ success: boolean; userId: number; bookingStatus: string }>(`/api/tours/${tourId}/candidates/${candidateId}/promote`, { method: 'POST' }),

  toggleUserReady: (userId: number) =>
    request<{ readyForTrips: boolean }>(`/api/users/${userId}/ready`, { method: 'PUT' }),

  getTemplates: () =>
    request<any[]>('/api/telegram/templates'),

  createTemplate: (data: { name: string; body: string; imageUrl?: string }) =>
    request<any>('/api/telegram/templates', { method: 'POST', body: JSON.stringify(data) }),

  updateTemplate: (id: number, data: { name?: string; body?: string; imageUrl?: string }) =>
    request<any>(`/api/telegram/templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteTemplate: (id: number) =>
    request<any>(`/api/telegram/templates/${id}`, { method: 'DELETE' }),

  sendTelegram: (bookingId: number, data: { templateId?: number; customText?: string; imageUrl?: string }) =>
    request<any>(`/api/bookings/${bookingId}/send-telegram`, { method: 'POST', body: JSON.stringify(data) }),

  sendTelegramBulk: (data: { tourId: number; templateId?: number; customText?: string; imageUrl?: string }) =>
    request<{ sent: number; skipped: number; errors: any[] }>('/api/bookings/send-telegram-bulk', { method: 'POST', body: JSON.stringify(data) }),

  sendSeatingTelegram: (tourId: number, data?: { driverId?: number }) =>
    request<{
      drivers: { sent: number; skipped: number; errors: Array<{ userId: number; name: string; error: string }> };
      passengers: { sent: number; skipped: number; errors: Array<{ userId: number; name: string; error: string }> };
      seatingNotifiedAt: string | null;
    }>(`/api/tours/${tourId}/send-seating-telegram`, {
      method: 'POST',
      body: JSON.stringify(data || {})
    }),

  getSeatingTelegramPreview: (tourId: number, driverId?: number) =>
    request<{
      drivers: Array<{ driverId: number; userId: number; name: string; willSend: boolean; skipReason: string | null; text: string | null }>;
      passengers: Array<{ driverId: number; userId: number; name: string; willSend: boolean; skipReason: string | null; text: string | null }>;
    }>(
      `/api/tours/${tourId}/seating-telegram-preview${driverId != null ? `?driverId=${driverId}` : ''}`
    ),

  telegramStatus: () => request<{ status: string }>('/api/telegram/status'),

  telegramConnect: (phone: string) =>
    request<{ status: string; message: string }>('/api/telegram/connect', {
      method: 'POST',
      body: JSON.stringify({ phone })
    }),

  telegramAuth: (data: { code?: string; password?: string }) =>
    request<{ status: string; message: string }>('/api/telegram/auth', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  telegramImport: (groupIds?: number[], groups?: { id: number; title: string | null }[]) =>
    request<{ total: number; imported: number; skipped: number; errors: { groupId: number; error: string }[]; groupNames?: { groupId: number; title: string; count: number }[] }>(
      '/api/telegram/import',
      { method: 'POST', body: JSON.stringify({ groupIds, groups }) }
    ),

  telegramGroups: () => request<{ groups: number[] }>('/api/telegram/groups'),

  telegramDialogs: () => request<{ dialogs: { id: number; title: string; type: string; members: number | null }[] }>('/api/telegram/dialogs'),

  telegramProxy: () =>
    request<{ type?: string; enabled?: boolean; server?: string; port?: number; username?: string; password?: string; secret?: string }>('/api/telegram/proxy'),

  telegramSetProxy: (data: { enabled: boolean; type: string; server: string; port: number; username?: string; password?: string; secret?: string }) =>
    request<{ status: string; message: string }>('/api/telegram/proxy', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  telegramTestProxy: (data: { enabled: boolean; type: string; server: string; port: number; username?: string; password?: string; secret?: string }) =>
    request<{ ok: boolean; latencyMs?: number; error?: string }>('/api/telegram/proxy/test', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  telegramDisconnect: () =>
    request<{ status: string; message: string }>('/api/telegram/disconnect', { method: 'POST' }),

  telegramDetectProxy: () =>
    request<{ found: boolean; source?: string; ip?: string; port?: number; socksType?: number }>('/api/telegram/detect-proxy'),

  telegramDeleteParticipants: () =>
    request<{ deleted: number }>('/api/telegram/participants', { method: 'DELETE' }),

  vkStatus: () =>
    request<{
      configured: boolean;
      connected: boolean;
      groups: any[];
      error?: string | null;
      community?: { configured: boolean; connected: boolean; error?: string | null };
      user?: { configured: boolean; connected: boolean; error?: string | null };
    }>('/api/vk/status'),

  vkSetToken: (token: string, sender: 'community' | 'user' = 'community') =>
    request<{
      connected: boolean;
      sender?: string;
      groups: any[];
      error?: string;
      message?: string;
      community?: { configured: boolean; connected: boolean; error?: string | null };
      user?: { configured: boolean; connected: boolean; error?: string | null };
    }>('/api/vk/token', {
      method: 'POST',
      body: JSON.stringify({ token, sender })
    }),

  vkDisconnect: (sender?: 'community' | 'user' | 'all') =>
    request<{
      status: string;
      connected?: boolean;
      community?: { configured: boolean; connected: boolean; error?: string | null };
      user?: { configured: boolean; connected: boolean; error?: string | null };
      groups?: any[];
    }>('/api/vk/disconnect', {
      method: 'POST',
      body: JSON.stringify({ sender: sender || 'all' })
    }),

  vkGroups: () =>
    request<{ groups: any[] }>('/api/vk/groups'),

  vkSaveGroups: (groupIds: string[]) =>
    request<{ saved: boolean; groups: string[] }>('/api/vk/groups', {
      method: 'POST',
      body: JSON.stringify({ groupIds })
    }),

  vkGroupMembers: (groupId: string, offset?: number, count?: number) =>
    request<{ members: any[]; count: number; offset: number; hasMore: boolean }>(
      `/api/vk/group/${groupId}/members?offset=${offset || 0}&count=${count || 100}`
    ),

  vkGroupInfo: (groupId: string) =>
    request<any>(`/api/vk/group/${groupId}/info`),

  vkImport: (groupIds: string[]) =>
    request<{ total: number; imported: number; skipped: number; errors: { groupId: string; error: string }[]; groupName: string }>(
      '/api/vk/import',
      { method: 'POST', body: JSON.stringify({ groupIds }) }
    ),

  vkSend: (data: { userIds?: number[]; message: string; sender?: 'community' | 'user' }) =>
    request<{ sent: number; failed: number; errors: { userId: number; error: string }[] }>(
      '/api/vk/send',
      { method: 'POST', body: JSON.stringify(data) }
    ),

  sendVk: (bookingId: number, data: { templateId?: number; customText?: string; sender?: 'community' | 'user' }) =>
    request<{ success: boolean; message: string }>(`/api/bookings/${bookingId}/send-vk`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  sendVkBulk: (data: { tourId: number; templateId?: number; customText?: string; sender?: 'community' | 'user' }) =>
    request<{ sent: number; skipped: number; errors: { userId: number; name?: string; error: string }[] }>(
      '/api/bookings/send-vk-bulk',
      { method: 'POST', body: JSON.stringify(data) }
    ),

  vkDeleteParticipants: () =>
    request<{ deleted: number }>('/api/vk/participants', { method: 'DELETE' }),

  getDrivers: () =>
    request<any[]>('/api/drivers'),

  createDriver: (data: { userId: number; carNumber: string; carMake: string; carModel: string; fuelConsumption: number; seats: number }) =>
    request<any>('/api/drivers', { method: 'POST', body: JSON.stringify(data) }),

  updateDriver: (id: number, data: any) =>
    request<any>(`/api/drivers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteDriver: (id: number) =>
    request<{ message: string }>(`/api/drivers/${id}`, { method: 'DELETE' }),

  assignDriverToTour: (driverId: number, tourId: number, pickupPoint?: string) =>
    request<any>(`/api/drivers/${driverId}/tours`, {
      method: 'POST',
      body: JSON.stringify({ tourId, pickupPoint })
    }),

  updateTourDriver: (driverId: number, tourId: number, data: { pickupPoint?: string }) =>
    request<any>(`/api/drivers/${driverId}/tours/${tourId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  removeDriverFromTour: (driverId: number, tourId: number) =>
    request<any>(`/api/drivers/${driverId}/tours/${tourId}`, { method: 'DELETE' }),

  adminAddBooking: (data: { tourId: number; userId: number; seats?: number; note?: string; status?: string }) =>
    request<any>('/api/bookings/admin', { method: 'POST', body: JSON.stringify(data) }),

  getUsers: () =>
    request<any[]>('/api/users'),

  createUser: (data: {
    name: string;
    email: string;
    password: string;
    phone?: string;
    vkId?: string | null;
    telegramId?: string;
    telegramUsername?: string;
  }) =>
    request<{
      id: number;
      name: string;
      email: string;
      phone: string | null;
      vkId: string | null;
      telegramId: string | null;
      telegramUsername: string | null;
      createdAt: string;
    }>('/api/users', {
      method: 'POST', body: JSON.stringify(data)
    }),

  getUser: (id: number) =>
    request<any>(`/api/users/${id}`),

  updateUserName: (id: number, name: string) =>
    request<{ id: number; name: string }>(`/api/users/${id}/name`, {
      method: 'PUT', body: JSON.stringify({ name })
    }),

  updateUserVkId: (id: number, vkId: string | null) =>
    request<{ id: number; vkId: string | null }>(`/api/users/${id}/vk`, {
      method: 'PUT', body: JSON.stringify({ vkId })
    }),

  updateUserTelegram: (
    id: number,
    data: { telegramId?: string | null; telegramUsername?: string | null }
  ) =>
    request<{ id: number; telegramId: string | null; telegramUsername: string | null }>(
      `/api/users/${id}/telegram`,
      { method: 'PUT', body: JSON.stringify(data) }
    ),

  updateUserPassword: (id: number, password: string) =>
    request<{ message: string }>(`/api/users/${id}/password`, {
      method: 'PUT', body: JSON.stringify({ password })
    }),

  deleteUser: (id: number) =>
    request<{ message: string }>(`/api/users/${id}`, { method: 'DELETE' }),

  verifyUser: (id: number, status: string) =>
    request<any>(`/api/users/${id}/verify`, { method: 'PUT', body: JSON.stringify({ status }) }),

  bulkVerifyUsers: (userIds: number[], status: string) =>
    request<{ updated: number }>('/api/users/bulk-verify', { method: 'POST', body: JSON.stringify({ userIds, status }) }),

  getVerifyStatuses: () =>
    request<{ statuses: string[]; labels: Record<string, string> }>('/api/users/verify-statuses'),

  getBookingsAll: () =>
    request<any[]>('/api/bookings/all'),

  updateBookingStatus: (id: number, status: string) =>
    request<any>(`/api/bookings/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),

  deleteBookingAdmin: (id: number) =>
    request<{ deleted: boolean }>(`/api/bookings/${id}/admin`, { method: 'DELETE' }),

  moveBooking: (id: number, tourId: number) =>
    request<any>(`/api/bookings/${id}/move`, { method: 'PUT', body: JSON.stringify({ tourId }) }),

  assignBookingDriver: (id: number, driverId: number | null, travelMode?: 'car' | 'bus') =>
    request<any>(`/api/bookings/${id}/assign-driver`, {
      method: 'PUT',
      body: JSON.stringify({ driverId, ...(travelMode ? { travelMode } : {}) })
    }),

  getToursList: () =>
    request<{ tours: ApiTour[]; total: number }>('/api/tours'),

  getParticipantsPreviews: (tourIds: number[]) =>
    request<Record<string, { avatars: (string | null)[]; count: number }>>(`/api/tours/participants-previews?tourIds=${tourIds.join(',')}`),

  getLocations: (params?: { search?: string; category?: string }) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.category && params.category !== 'all') qs.set('category', params.category);
    return request<{ locations: Location[]; total: number }>(`/api/locations/all?${qs}`);
  },

  getLocation: (idOrSlug: string) => request<Location>(`/api/locations/${idOrSlug}`),

  createLocation: (data: LocationInput) =>
    request<Location>('/api/locations', { method: 'POST', body: JSON.stringify(data) }),

  updateLocation: (id: number, data: Partial<LocationInput>) =>
    request<Location>(`/api/locations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteLocation: (id: number) =>
    request<{ deleted: boolean }>(`/api/locations/${id}`, { method: 'DELETE' }),

  getPipeline: (tourId?: number) => {
    const qs = tourId ? `?tourId=${tourId}` : '';
    return request<PipelineData>(`/api/bookings/pipeline${qs}`);
  },

  movePipelineStage: (bookingId: number, stage: string, note?: string) =>
    request<ApiTour & { pipelineLog: any[] }>(`/api/bookings/${bookingId}/pipeline`, {
      method: 'PUT',
      body: JSON.stringify({ stage, note })
    }),

  getLoginHistory: (params?: {
    userId?: number;
    appTarget?: string;
    loginMethod?: string;
    success?: boolean;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.userId !== undefined) qs.set('userId', String(params.userId));
    if (params?.appTarget) qs.set('appTarget', params.appTarget);
    if (params?.loginMethod) qs.set('loginMethod', params.loginMethod);
    if (params?.success !== undefined) qs.set('success', String(params.success));
    if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params?.dateTo) qs.set('dateTo', params.dateTo);
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    const queryString = qs.toString();
    return request<{ items: AuthLoginHistoryItem[] }>(`/api/admins/login-history${queryString ? `?${queryString}` : ''}`);
  },

  getOperationLog: (params?: {
    actorUserId?: number;
    entityType?: string;
    actionType?: string;
    appTarget?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.actorUserId !== undefined) qs.set('actorUserId', String(params.actorUserId));
    if (params?.entityType) qs.set('entityType', params.entityType);
    if (params?.actionType) qs.set('actionType', params.actionType);
    if (params?.appTarget) qs.set('appTarget', params.appTarget);
    if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params?.dateTo) qs.set('dateTo', params.dateTo);
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    const queryString = qs.toString();
    return request<{ items: OperationLogItem[] }>(`/api/admins/operation-log${queryString ? `?${queryString}` : ''}`);
  },

  getAdmins: () =>
    request<AdminUser[]>('/api/admins'),

  createAdmin: (data: { name: string; email: string; password: string; role: string }) =>
    request<AdminUser>('/api/admins', { method: 'POST', body: JSON.stringify(data) }),

  promoteAdmin: (data: { userId: number; role: string }) =>
    request<AdminUser>('/api/admins/promote', { method: 'POST', body: JSON.stringify(data) }),

  updateAdmin: (id: number, data: { name?: string; email?: string; role?: string; password?: string }) =>
    request<AdminUser>(`/api/admins/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteAdmin: (id: number) =>
    request<{ message: string }>(`/api/admins/${id}`, { method: 'DELETE' }),

  getRoles: () =>
    request<Role[]>('/api/roles'),

  getPermissions: () =>
    request<PermissionGroup[]>('/api/roles/permissions'),

  createRole: (data: { name: string; permissions: string[] }) =>
    request<Role>('/api/roles', { method: 'POST', body: JSON.stringify(data) }),

  updateRole: (id: number, data: { name?: string; permissions?: string[] }) =>
    request<Role>(`/api/roles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteRole: (id: number) =>
    request<{ message: string }>(`/api/roles/${id}`, { method: 'DELETE' })
};
